
import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus, Edit, Save, X } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import ScadaConnectorForm from './ScadaConnectorForm';
import { useToast } from '@/hooks/use-toast';

interface ProtocolHandlersSettingsProps {
  editing: boolean;
  onEditToggle: () => void;
  onSave: () => void;
}

const protocols = [
  { id: '1', name: 'OPC UA', description: 'Open Platform Communications Unified Architecture' },
  { id: '2', name: 'Modbus', description: 'Modbus TCP and RTU protocols' },
  { id: '3', name: 'BACnet', description: 'Building Automation and Control Networks' },
  { id: '4', name: 'DNP3', description: 'Distributed Network Protocol 3' },
  { id: '5', name: 'MQTT', description: 'Message Queuing Telemetry Transport' }
];

const ProtocolHandlersSettings: React.FC<ProtocolHandlersSettingsProps> = ({ 
  editing, 
  onEditToggle, 
  onSave 
}) => {
  const [activeTab, setActiveTab] = useState('protocols');
  const [addingConnector, setAddingConnector] = useState(false);
  const { toast } = useToast();

  const handleAddConnector = () => {
    setAddingConnector(true);
  };

  const handleCancelAddConnector = () => {
    setAddingConnector(false);
  };

  const handleSaveConnector = (values: any) => {
    console.log('Saving connector:', values);
    toast({
      title: "Protocol Handler Added",
      description: `${values.name} has been successfully added.`
    });
    setAddingConnector(false);
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Protocol Handlers</h2>
          <p className="text-muted-foreground">
            Configure communication protocols for device integration
          </p>
        </div>
        {!addingConnector && (
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={onEditToggle} className="gap-1">
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button onClick={onSave} className="gap-1">
                  <Save className="h-4 w-4" />
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleAddConnector} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Add Protocol Handler
                </Button>
                <Button variant="outline" onClick={onEditToggle} className="gap-1">
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {addingConnector ? (
          <div className="bg-muted p-4 rounded-md">
            <h3 className="text-lg font-semibold mb-4">Add New Protocol Handler</h3>
            <ScadaConnectorForm 
              onSubmit={handleSaveConnector}
              onCancel={handleCancelAddConnector}
            />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid grid-cols-3 sm:w-auto w-full">
              <TabsTrigger value="protocols">Available Protocols</TabsTrigger>
              <TabsTrigger value="connectors">Active Connectors</TabsTrigger>
              <TabsTrigger value="templates">Device Templates</TabsTrigger>
            </TabsList>
            
            <TabsContent value="protocols" className="space-y-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Protocol</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {protocols.map((protocol) => (
                    <TableRow key={protocol.id}>
                      <TableCell className="font-medium">{protocol.name}</TableCell>
                      <TableCell>{protocol.description}</TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                          Available
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
            
            <TabsContent value="connectors" className="space-y-6">
              <div className="flex flex-col space-y-4">
                <div className="border rounded-md p-6 bg-muted/50">
                  <div className="flex items-center justify-center h-24">
                    <div className="text-center">
                      <p className="text-muted-foreground mb-2">No active protocol connectors configured</p>
                      <Button onClick={handleAddConnector} variant="outline" className="gap-1">
                        <Plus className="h-4 w-4" />
                        Add First Connector
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="templates" className="space-y-6">
              <div className="flex flex-col space-y-4">
                <div className="border rounded-md p-6 bg-muted/50">
                  <div className="flex items-center justify-center h-24">
                    <div className="text-center">
                      <p className="text-muted-foreground mb-2">No device templates configured</p>
                      <Button variant="outline" disabled className="gap-1">
                        <Plus className="h-4 w-4" />
                        Add Device Template
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default ProtocolHandlersSettings;
